import { gql } from "@apollo/client";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import Input from "components/common/form/inputs/Input";
import FormActions from "components/common/form/partials/FormActions";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { IdName } from "types/common-types";
import { AccommodationMaintenanceTypeFromDataQuery, AccommodationMaintenanceTypeFromDataQueryVariables, AccommodationMaintenanceTypeFromMutation, AccommodationMaintenanceTypeFromMutationVariables } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";

type AccommodationMaintenanceTypeFromPropsType = {
    model?: number | string | IdName,
    name: string,
    onSubmit?: (model: IdName) => void
}

const query = gql`
query AccommodationMaintenanceTypeFromData($id: Int!) {
    accommodationMaintenanceType(id: $id) {
      id
      name
    }
  }
`;

const mutation = gql`
mutation AccommodationMaintenanceTypeFrom($model: AccommodationMaintenanceTypeInput!) {
    accommodationMaintenanceType(model: $model){
      id,
      name
    }
  }
`;

function getId(model?: number | string | IdName) {
    if(!model) return 0;
    if(typeof model === 'number') return model;
    if(typeof model === 'string') return parseInt(model) || 0;
    return model.id;
}

const AccommodationMaintenanceTypeFrom = (props: AccommodationMaintenanceTypeFromPropsType) => {
    const context = useAppContext();
    const [model, setModel] = useState<IdName>({
        id: getId(props.model),
        name: ''
    });
    const [componentState, setComponentState] = useState<ComponentStateTypes>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if(model.id) {
            load();
        }
    }, []);

    const load = async () => {
        setComponentState('loading');
        try {
            const response = await graph<AccommodationMaintenanceTypeFromDataQuery, AccommodationMaintenanceTypeFromDataQueryVariables>({
                query,
                variables: {
                    id: model.id
                }
            });

            if(response.errors) {
                throw response.errors;
            }

            if(!response.data?.accommodationMaintenanceType) {
                setComponentState('no-data');
            }

            setModel({
                id: response.data?.accommodationMaintenanceType?.id || 0,
                name: response.data?.accommodationMaintenanceType?.name || ''
            });

            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }

    const onSubmit = async () => {
        setIsSubmitting(true);
        
        try {
            const response = await graph<AccommodationMaintenanceTypeFromMutation, AccommodationMaintenanceTypeFromMutationVariables>({
                query: mutation,
                variables: {
                    model: {
                        id: model.id || 0,
                        name: model.name
                    }
                }
            });

            if(response.errors) {
                throw response.errors;
            }

            if(props.onSubmit) {
                props.onSubmit({
                    id: response.data?.accommodationMaintenanceType?.id || 0,
                    name: response.data?.accommodationMaintenanceType?.name || ''
                });
            } else {
                context.navigate(-1);
            }

            context.notify('success', 'request.save.success');
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <ComponentState state={componentState}>
            <Form name={props.name + "-AccommodationMaintenanceTypeFrom"} onSubmit={onSubmit}>
                <fieldset>
                    <Input
                        name="name"
                        label="fields.name.name"
                        required
                        value={model.name}
                        onChange={(e) => setModel({ ...model, name: e || '' })}
                    />
                </fieldset>
                <FormActions disableInputs={isSubmitting} />
            </Form>
        </ComponentState>
    );
};

export default AccommodationMaintenanceTypeFrom;
